// @oasis-permission-inherits: /capcode (capcode:view)
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Pause, Play, Save, Send } from "lucide-react";
import * as React from "react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { ChatSurface } from "~/modules/cap-code/components/chat-surface";
import {
  activateAutomation,
  fetchAutomation,
  fetchAutomationRuns,
  listConversationEventsQuery,
  listConversationMessagesQuery,
  listConversationTurnsQuery,
  pauseAutomation,
  resumeAutomation,
  runAutomationNow,
  testAutomation,
  updateAutomation,
} from "~/modules/cap-code/network";
import type { AutomationRecord, AutomationRunRecord } from "~/modules/cap-code/schemas";

export const Route = createFileRoute("/_dash/capcode/automations/$automationId")({
  component: AutomationDetailRoute,
});

function formatDate(timestamp: number | null): string {
  return timestamp ? new Date(timestamp).toLocaleString() : "None";
}

function statusClass(status: string): string {
  if (status === "active" || status === "completed") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (status === "draft") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (status === "failed") {
    return "border-red-200 bg-red-50 text-red-700";
  }
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function metadataString(run: AutomationRunRecord, key: string): string | null {
  const value = run.metadata[key];
  return typeof value === "string" && value.trim() ? value : null;
}

function upsertRun(runs: AutomationRunRecord[], run: AutomationRunRecord): AutomationRunRecord[] {
  const byId = new Map(runs.map((item) => [item.id, item]));
  byId.set(run.id, run);
  return [...byId.values()].sort((left, right) => right.createdAt - left.createdAt);
}

function AutomationDetailRoute() {
  const { automationId } = Route.useParams();
  const queryClient = useQueryClient();
  const [automation, setAutomation] = React.useState<AutomationRecord | null>(null);
  const [runs, setRuns] = React.useState<AutomationRunRecord[]>([]);
  const [title, setTitle] = React.useState("");
  const [scheduleText, setScheduleText] = React.useState("");
  const [timezone, setTimezone] = React.useState("");
  const [prompt, setPrompt] = React.useState("");
  const [memoryText, setMemoryText] = React.useState("");
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextAutomation, nextRuns] = await Promise.all([
        fetchAutomation(automationId),
        fetchAutomationRuns(automationId),
      ]);
      setAutomation(nextAutomation);
      setRuns(nextRuns);
      setTitle(nextAutomation.title);
      setScheduleText(nextAutomation.scheduleText);
      setTimezone(nextAutomation.timezone);
      setPrompt(nextAutomation.prompt);
      setMemoryText(nextAutomation.memoryText);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load automation");
    } finally {
      setLoading(false);
    }
  }, [automationId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (runs.length === 0) {
      setSelectedRunId(null);
      return;
    }
    if (!selectedRunId || !runs.some((run) => run.id === selectedRunId)) {
      setSelectedRunId(runs[0].id);
    }
  }, [runs, selectedRunId]);

  const hasActiveRun = React.useMemo(
    () => runs.some((run) => run.status === "queued" || run.status === "running"),
    [runs]
  );

  React.useEffect(() => {
    if (!hasActiveRun) {
      return;
    }
    const timer = window.setInterval(() => {
      void fetchAutomationRuns(automationId)
        .then(setRuns)
        .catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [automationId, hasActiveRun]);

  const runsChronological = React.useMemo(() => [...runs].reverse(), [runs]);
  const runVersionById = React.useMemo(
    () => new Map(runsChronological.map((run, index) => [run.id, index + 1])),
    [runsChronological]
  );
  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? null;

  const save = async () => {
    setBusy("save");
    setError(null);
    try {
      await updateAutomation(automationId, {
        title,
        scheduleText,
        timezone,
        prompt,
        memoryText,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save automation");
    } finally {
      setBusy(null);
    }
  };

  const toggleStatus = async () => {
    if (!automation) {
      return;
    }
    if (automation.status === "draft") {
      return;
    }
    setBusy("status");
    setError(null);
    try {
      if (automation.status === "active") {
        await pauseAutomation(automation.id);
      } else {
        await resumeAutomation(automation.id);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update automation");
    } finally {
      setBusy(null);
    }
  };

  const testDraft = async () => {
    setBusy("test");
    setError(null);
    try {
      const result = await testAutomation({
        automationId,
        title,
        prompt,
        scheduleText,
        timezone,
        memoryText,
      });
      if ("supported" in result) {
        setError(result.supported ? "Schedule parsed without starting a test." : result.error);
        return;
      }

      const conversationParams = { pathParams: { conversationId: result.conversation.id } };
      const eventsParams = { ...conversationParams, params: {} };
      listConversationMessagesQuery.setQueryData(queryClient, conversationParams, [result.message]);
      listConversationTurnsQuery.setQueryData(queryClient, conversationParams, [result.turn]);
      listConversationEventsQuery.setQueryData(queryClient, eventsParams, []);
      void listConversationEventsQuery.invalidateQuery(queryClient, eventsParams);

      setAutomation(result.automation);
      setRuns((current) => upsertRun(current, result.run));
      setSelectedRunId(result.run.id);
      void load();
    } catch {
      setError("Failed to run automation test.");
    } finally {
      setBusy(null);
    }
  };

  const activate = async () => {
    setBusy("activate");
    setError(null);
    try {
      await updateAutomation(automationId, {
        title,
        scheduleText,
        timezone,
        prompt,
        memoryText,
      });
      await activateAutomation(automationId);
      await load();
    } catch {
      setError("Failed to create automation.");
    } finally {
      setBusy(null);
    }
  };

  const runNow = async () => {
    setBusy("run-now");
    setError(null);
    try {
      await runAutomationNow(automationId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to run automation");
    } finally {
      setBusy(null);
    }
  };

  if (loading && !automation) {
    return (
      <div className="h-[calc(100dvh-57px)] overflow-y-auto bg-background p-6 text-sm text-muted-foreground">
        Loading automation...
      </div>
    );
  }

  return (
    <div className="h-[calc(100dvh-57px)] overflow-hidden bg-background">
      <div className="grid h-full min-h-0 w-full gap-4 px-4 py-4 lg:grid-cols-[360px_minmax(0,1fr)] 2xl:grid-cols-[380px_minmax(0,1fr)]">
        <main className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-white">
          <header className="shrink-0 border-b border-border p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <h1 className="truncate text-xl font-semibold tracking-tight">
                    {automation?.title ?? "Automation"}
                  </h1>
                  {automation && (
                    <Badge className={statusClass(automation.status)}>{automation.status}</Badge>
                  )}
                </div>
                {automation && (
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    {automation.scheduleSummary}
                    <br />
                    {automation.timezone} · Next {formatDate(automation.nextRunAt)}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              {automation?.status === "draft" ? (
                <>
                  <Button
                    className="flex-1"
                    variant="outline"
                    onClick={() => void testDraft()}
                    disabled={busy != null}
                  >
                    <Play size={16} />
                    Test
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => void activate()}
                    disabled={busy != null || runs.length === 0}
                  >
                    <Check size={16} />
                    Create
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    className="flex-1"
                    variant="outline"
                    onClick={() => void toggleStatus()}
                    disabled={busy != null}
                  >
                    {automation?.status === "active" ? <Pause size={16} /> : <Play size={16} />}
                    {automation?.status === "active" ? "Pause" : "Resume"}
                  </Button>
                  <Button className="flex-1" onClick={() => void runNow()} disabled={busy != null}>
                    <Send size={16} />
                    Run now
                  </Button>
                </>
              )}
            </div>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {error && (
              <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="automation-title">Title</Label>
                <Input
                  id="automation-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-[1fr_220px]">
                <div className="space-y-2">
                  <Label htmlFor="automation-schedule">Schedule</Label>
                  <Input
                    id="automation-schedule"
                    value={scheduleText}
                    onChange={(e) => setScheduleText(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <div className="flex h-10 items-center rounded-md border border-border bg-muted/30 px-3 text-sm text-muted-foreground">
                    {timezone}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="automation-prompt">Prompt</Label>
                <Textarea
                  id="automation-prompt"
                  className="min-h-40"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="automation-memory">Memory</Label>
                <Textarea
                  id="automation-memory"
                  className="min-h-24"
                  value={memoryText}
                  onChange={(e) => setMemoryText(e.target.value)}
                />
              </div>
              <Button className="w-full" onClick={() => void save()} disabled={busy != null}>
                <Save size={16} />
                Save
              </Button>
            </div>
          </div>
        </main>

        <section className="min-h-0 overflow-hidden rounded-lg border border-border bg-white">
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <div className="shrink-0 border-b border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold">Test runs</h2>
                {selectedRun && (
                  <Badge className={statusClass(selectedRun.status)}>{selectedRun.status}</Badge>
                )}
              </div>
              {runs.length === 0 && (
                <p className="mt-3 text-sm text-muted-foreground">No automation runs yet.</p>
              )}
              {runs.length > 0 && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                  {runsChronological.map((run) => {
                    const isSelected = run.id === selectedRunId;
                    const version = runVersionById.get(run.id) ?? 1;
                    return (
                      <button
                        key={run.id}
                        type="button"
                        onClick={() => setSelectedRunId(run.id)}
                        className={[
                          "shrink-0 rounded-md border px-3 py-2 text-left text-xs transition",
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-background text-foreground hover:bg-muted",
                        ].join(" ")}
                      >
                        <span className="block font-medium">
                          {metadataString(run, "kind") === "test" ? "Test" : "Run"} v{version}
                        </span>
                        <span
                          className={
                            isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                          }
                        >
                          {run.status} · {new Date(run.scheduledFor).toLocaleTimeString()}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedRun?.conversationId ? (
              <>
                <div className="border-b border-border bg-muted/30 p-4 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      Started {formatDate(selectedRun.startedAt ?? selectedRun.scheduledFor)}
                    </span>
                    {metadataString(selectedRun, "scheduleSummary") && (
                      <span>{metadataString(selectedRun, "scheduleSummary")}</span>
                    )}
                  </div>
                  {metadataString(selectedRun, "prompt") && (
                    <p className="mt-2 max-h-24 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-white p-2 text-xs text-muted-foreground">
                      {metadataString(selectedRun, "prompt")}
                    </p>
                  )}
                </div>
                <ChatSurface
                  key={selectedRun.conversationId}
                  conversationId={selectedRun.conversationId}
                  mode="embedded"
                  showComposer={false}
                  showEventPanel={false}
                  showHistoryButton={false}
                  className="min-h-0 flex-1"
                  contentClassName="gap-3"
                />
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                Run a test to see the full conversation output here.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
